import { User } from '../types/auth';

/**
 * Synchronous sessionStorage cache for the app's first-load bootstrap data
 * (the current user `/me` and the user's permissions).
 *
 * Why sessionStorage (not localStorage): this data is read in the
 * AuthProvider/PermissionProvider mount effects, which run BEFORE the React
 * Query persist provider finishes restoring its cache — so it must be
 * synchronous. sessionStorage is synchronous AND wiped when the tab/browser
 * closes, so the user's identity + permissions aren't left on disk between
 * browser sessions. It still survives a refresh, so the app paints instantly.
 * Both values are always revalidated against the server right after (the server
 * still enforces RBAC via the httpOnly cookie, so a stale/edited cache can
 * never grant access it shouldn't).
 *
 * Cleared on login AND logout so the next user of the browser starts clean.
 * NEVER store the CSRF token or chat ws-token here — those must stay fresh.
 */
const ME_KEY = 'ERP_MAF_ME';
const PERMS_KEY = 'ERP_MAF_PERMS';

// One-time cleanup: earlier builds wrote these to localStorage. Purge any
// on-disk copy left over from before the sessionStorage migration.
try {
  window.localStorage.removeItem(ME_KEY);
  window.localStorage.removeItem(PERMS_KEY);
} catch {
  /* noop */
}

export function readCachedMe(): User | null {
  try {
    const raw = sessionStorage.getItem(ME_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

export function writeCachedMe(user: User): void {
  try {
    sessionStorage.setItem(ME_KEY, JSON.stringify(user));
  } catch {
    /* quota / disabled storage — non-fatal */
  }
}

export function readCachedPerms(): any | null {
  try {
    const raw = sessionStorage.getItem(PERMS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeCachedPerms(data: any): void {
  try {
    sessionStorage.setItem(PERMS_KEY, JSON.stringify(data));
  } catch {
    /* quota / disabled storage — non-fatal */
  }
}

export function clearBootstrapCache(): void {
  try {
    sessionStorage.removeItem(ME_KEY);
    sessionStorage.removeItem(PERMS_KEY);
    // also drop any legacy on-disk copies
    localStorage.removeItem(ME_KEY);
    localStorage.removeItem(PERMS_KEY);
  } catch {
    /* noop */
  }
}
